import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Building2, Landmark, StarHalf, TrendingUpDown, Workflow } from 'lucide-react';

import { getLocale, t } from '@/i18n';
import { cn, Button } from '@/shared';
import { normalizePathname } from '@/shared/lib/utils/pathname';

import { insightsHomePath } from '@/entities/finance-content/lib/content-lang';

import type { I18nKey } from '@/i18n';

interface TabItem {
  /**
   * 🔴 문구가 아니라 **카탈로그 키**를 든다 (TRAD-2118 ①).
   * 이 배열은 **모듈 스코프 상수**라 여기서 `t()` 를 부르면 **최초 평가 시점 로케일에 고정**되어
   * 언어를 바꿔도 안 바뀐다(TRAD-2057 에서 사이드바가 정확히 그렇게 깨졌다).
   * 그래서 키만 두고 **렌더 시점에** `t(labelKey)` 로 푼다.
   */
  labelKey: I18nKey;
  path: string;
  icon: JSX.Element;
  matchPrefix?: string;
  /**
   * TRAD-3921 — 화면 언어에 따라 주소가 달라지는 항목(인사이트: 영어 화면 = `/en/insights`). 없으면 `path` 그대로.
   * 라벨과 같은 이유로 «값»이 아니라 «함수»를 둔다 — 모듈 스코프 상수에서 로케일을 읽으면 최초 평가 시점에 굳는다.
   */
  localePath?: (locale: string) => string;
}

/** 항목이 실제로 가는 주소 — 화면 언어를 «렌더 시점에» 넘긴다. 순수 함수(테스트용 export). */
export const resolveTabPath = (item: Pick<TabItem, 'path' | 'localePath'>, locale: string): string =>
  item.localePath ? item.localePath(locale) : item.path;

// TRAD-1979 — 테스트에서 라벨 경로를 고정하기 위해 export 한다.
// 배열이 모듈 밖에서 안 보이면 "라벨이 카탈로그를 탄다"를 소스 grep 으로만 확인하게 되고,
// grep 은 키가 카탈로그에 **실재하는지**까지는 못 본다(키 오타 = 화면에 raw 키 노출).
export const mainTabs: TabItem[] = [
  {
    labelKey: 'nav.home',
    path: '/',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.tab.market',
    path: '/market',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M3 13h2v8H3v-8zm4-4h2v12H7V9zm4-6h2v18h-2V3zm4 8h2v10h-2V11zm4-3h2v13h-2V8z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.community.label',
    path: '/board/idea',
    matchPrefix: '/board',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.tab.watchlist',
    path: '/interest',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ),
  },
];

export const moreMenuItems: TabItem[] = [
  {
    // TRAD-2115④ — nav.prediction.label("예측시장")은 서비스명 본문 표기라 그대로 두고,
    // 내비 라벨 전용 분리 키로 바꾼다(팀장 승인 2026-08-10). 회귀가드 참고: 이 파일
    // market-terminology-bucket-c.test.ts가 nav.prediction.label을 "절대 치환 금지"로
    // 고정하는 이유도 같은 원칙 — 서비스명과 내비 라벨을 같은 키로 묶으면 한쪽을 바꿀 때
    // 다른 쪽이 의도치 않게 따라 바뀐다.
    labelKey: 'nav.prediction.tab_label',
    path: '/polymarket',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M13 7.83c.85-.3 1.53-.98 1.83-1.83H18l-3 7c0 1.66 1.34 3 3 3s3-1.34 3-3l-3-7h2V4h-6.17C13.42 2.83 12.8 2 12 2s-1.42.83-1.83 2H4v2h2l-3 7c0 1.66 1.34 3 3 3s3-1.34 3-3L6 7h3.17c.3.85.98 1.53 1.83 1.83V19H7v2h10v-2h-4V7.83zM20.37 14h-4.74l2.37-5.5 2.37 5.5zm-13 0H2.63L5 8.5l2.37 5.5z" />
      </svg>
    ),
  },
  // TRAD-2734 — 금융(캘린더·지표)을 사이드바와 같은 정책으로 부동산보다 앞에 배치한다.
  // 🔴 이 more 메뉴는 그룹 개념이 없는 flat 그리드라 사이드바의 '금융' 묶음을 재현할 수 없다.
  //    따라서 두 내비가 어긋난 상태로 배포된다 — 발산 자체는 TRAD-2736 으로 추적한다.
  {
    labelKey: 'nav.tab.financial_calendar',
    path: '/financial-calendar',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13zM8 10H6v2h2v-2zm0 4H6v2h2v-2zm4-4h-2v2h2v-2zm0 4h-2v2h2v-2zm4-4h-2v2h2v-2zm0 4h-2v2h2v-2z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.tab.economic_indicators',
    path: '/economic-indicators',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
      </svg>
    ),
  },
  {
    // TRAD-3582 — 미국 기관 보유(13F). 금융 묶음(캘린더·지표) 바로 뒤. 더보기 그리드 한 칸(하단바 4슬롯과 경쟁하지 않는다).
    labelKey: 'nav.tab.institutions',
    path: '/institutions',
    icon: <Landmark className="w-5 h-5" />,
  },
  {
    // TRAD-3867(10/30 davidyoon «인사이트» 별도 섹션) — PC 상단 내비와 같은 자리(금융 묶음 바로 다음)·같은 라벨 키. 종전엔 모바일 진입 경로 0.
    // 아이콘은 종전 PC 메뉴 항목이 쓰던 lucide Workflow(관계 그림) — 더보기 그리드의 다른 아이콘과 겹치지 않는다.
    // TRAD-3921 — 영어 화면이면 영어 목록 `/en/insights` 로(PC 상단 내비와 같은 규칙 · insightsHomePath).
    labelKey: 'nav.insights',
    path: '/insights',
    localePath: insightsHomePath,
    icon: <Workflow className="w-5 h-5" />,
  },
  {
    // TRAD-3410 — designer 실측: 이 아이콘이 홈(nav.home)의 집 모양과 svg 바이트
    // 동일했다(더보기 그리드를 열면 탭바와 나란히 보여 목적지 식별이 안 됨). #26
    // (Material filled → lucide outline 전환) 결정과 무관하게 지금 세트 안에서
    // 서로 다른 그림이어야 하는 식별 결함이라 먼저 고친다 — 후보는 AppTopNav.tsx가
    // 다른 목적지(부동산 커뮤니티, /community/real-estate)에 이미 쓰는 lucide
    // `Building2`. 🔴 나머지 13개는 손그림 filled 아이콘이라 이 두 개(이 항목 +
    // 아래 kimchi_premium)만 lucide outline 스타일이 되어 시각적으로 이질적이다 —
    // #26이 세트 전체를 lucide로 통일하기 전까지의 과도기 상태로 받아들인다.
    labelKey: 'nav.real_estate.label',
    path: '/real-estate',
    icon: <Building2 className="w-5 h-5" />,
  },
  {
    // TRAD-3410 — designer 실측: 이 아이콘이 경제 지표(nav.tab.economic_indicators)의
    // 꺾은선과 svg 바이트 동일했다. AppTopNav.tsx가 같은 목적지(/kimchi-premium)에
    // 이미 쓰는 lucide `TrendingUpDown`을 그대로 재사용한다(위 부동산 항목과 같은
    // 과도기 스타일 트레이드오프).
    labelKey: 'nav.tab.kimchi_premium',
    path: '/kimchi-premium',
    icon: <TrendingUpDown className="w-5 h-5" />,
  },
  {
    labelKey: 'nav.crypto.ai_report',
    path: '/crypto-report',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
      </svg>
    ),
  },
  {
    // TRAD-3796(2026-10-22, davidyoon «코인 평가는 암호화폐 메뉴로») — 모바일 «신규 추가». 종전엔 더보기에 /evaluation 이 «아예 없어»
    // 모바일 진입 경로가 0 이었다(TRAD-3192 는 PC 상단 내비에만 넣었다). PC 상단 내비의 «암호화폐» 그룹과 같은 자리(AI 리포트 뒤)·
    // 같은 라벨 키(evaluation.meta.title)·같은 아이콘(lucide StarHalf — AppTopNav 와 동일, 관심종목 Star 와 구분).
    labelKey: 'evaluation.meta.title',
    path: '/evaluation',
    icon: <StarHalf className="w-5 h-5" />,
  },
  {
    labelKey: 'nav.news.label',
    path: '/news',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10zM6 10h8v2H6v-2zm0 4h8v2H6v-2zm10-4h2v6h-2v-6z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.tab.portfolio',
    path: '/portfolio',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.my_page',
    path: '/my-page',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.tab.notice',
    path: '/notice',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM11 5h2v6h-2zm0 8h2v2h-2z" />
      </svg>
    ),
  },
];

/**
 * 경로 활성 판정 (순수 함수 — 단위 테스트용 export).
 * TRAD-2102 — 운영 인프라가 끝 슬래시로 리다이렉트하므로 `===` 비교 전 정규화한다.
 * 끝 슬래시가 없는 입력에는 항등 함수이므로 기존 동작은 그대로다.
 */
export const computeTabActive = (rawPathname: string, path: string): boolean => {
  const pathname = normalizePathname(rawPathname);
  if (path === '/') return pathname === '/';
  return pathname.startsWith(path);
};

export const BottomTabBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (tab: TabItem) => computeTabActive(location.pathname, tab.matchPrefix ?? tab.path);

  const isPathActive = (path: string) => computeTabActive(location.pathname, path);

  // TRAD-3921 — 언어별 주소가 있는 항목은 «지금 가는 주소»로도 켜진다(`/en/insights…` 는 `/insights` 로 시작하지 않는다).
  const locale = getLocale();
  const isItemActive = (item: TabItem) => isPathActive(item.path) || isPathActive(resolveTabPath(item, locale));

  const isMoreActive = moreMenuItems.some(isItemActive);

  return (
    <>
      {/*
        TRAD-3069 — 상단 내비(AppTopNav)가 md(768)가 아니라 lg(1024)부터 뜬다(라벨이 768~
        1023px에서 두 줄로 접히는 실측 때문 — MainLayout.tsx 주석 참고). 이 컴포넌트의
        breakpoint를 md:hidden으로 그대로 두면 768~1023px 구간에 내비가 하나도 없는 빈
        구간이 생기므로 lg:hidden으로 맞췄다.
      */}
      {/* 더보기 메뉴 오버레이 */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMoreOpen(false)}>
          <div
            className="absolute bottom-14 left-0 right-0 bg-white dark:bg-background border-t border-border shadow-lg safe-area-bottom"
            onClick={e => e.stopPropagation()}
          >
            <div className="grid grid-cols-4 gap-1 p-3">
              {moreMenuItems.map(item => {
                const active = isItemActive(item);
                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    type="button"
                    className={cn(
                      'h-auto w-full flex-col gap-1 rounded-lg px-0 py-3 transition-colors',
                      // 🔴 text-brand-text(단독 텍스트 전용 토큰) — bg-primary(#7C3AED) 그대로
                      // 안 쓴다. 다크에서 #9864F1로 갈리는 게 정상이다(WCAG 대비 상향, index.css
                      // --brand-text 정의부 주석 참고) — "다른 탭 표면과 색이 다르다"고
                      // bg-primary/text-primary로 되돌리면 그 대비 수정이 조용히 사라진다.
                      active ? 'text-brand-text bg-primary/10' : 'text-muted-foreground hover:bg-secondary',
                    )}
                    onClick={() => {
                      navigate(resolveTabPath(item, locale));
                      setMoreOpen(false);
                    }}
                  >
                    {item.icon}
                    <span className="text-2xs font-medium">{t(item.labelKey)}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 하단 탭바 */}
      <nav data-chrome className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-background border-t border-border z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-14">
          {mainTabs.map(tab => {
            const active = isActive(tab);
            return (
              <Button
                key={tab.path}
                variant="ghost"
                type="button"
                className={cn(
                  'h-full flex-col flex-1 gap-0.5 px-0 transition-colors hover:bg-transparent',
                  active ? 'text-brand-text' : 'text-muted-foreground',
                )}
                onClick={() => {
                  navigate(tab.path);
                  setMoreOpen(false);
                }}
              >
                {tab.icon}
                <span className="text-2xs font-medium">{t(tab.labelKey)}</span>
              </Button>
            );
          })}

          {/* 더보기 버튼 */}
          <Button
            variant="ghost"
            type="button"
            className={cn(
              'h-full flex-col flex-1 gap-0.5 px-0 transition-colors hover:bg-transparent',
              moreOpen || isMoreActive ? 'text-brand-text' : 'text-muted-foreground',
            )}
            onClick={() => setMoreOpen(prev => !prev)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            <span className="text-2xs font-medium">{t('nav.tab.more')}</span>
          </Button>
        </div>
      </nav>
    </>
  );
};
